import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import api from "../../chat/api";
import {
  DATE_PRESETS,
  emptyDateFilter,
  parseProjectDate,
  toISODate,
  dateFilterLabel,
} from "./projectDateUtils";
import { PROJECT_SOURCES, normalizeSource } from "./projectAccess";

// Fallback status options (used until the org's configured statuses load, or if
// the request fails). The backend stores "Win", which we surface as "Won";
// everything else maps 1:1 to the stored rfp_status value.
const FALLBACK_STATUS_OPTIONS = [
  { label: "Draft", value: "Draft" },
  { label: "Submitted", value: "Submitted" },
  { label: "Missed", value: "Missed" },
  { label: "Won", value: "Win" },
  { label: "Lost", value: "Lost" },
];

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);


// ─── A single "select-style" row inside the Filters panel ──────────────────────
const FilterRow = ({ label, value, isActive, onClick }) => (
  <div className="relative">
    <span className="block text-[13px] text-[#00000080] mb-1.5 font-medium">{label}</span>
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg border text-[14px] font-medium transition-colors ${
        isActive
          ? "border-[#8B55DD] text-[#1A1A1A] bg-white ring-1 ring-[#8B55DD]/30"
          : "border-[#E5E7EB] text-[#1A1A1A] bg-white hover:bg-[#F5F5F5] hover:border-[#D1D5DB]"
      }`}
    >
      <span className="truncate text-left">{value}</span>
      <ChevronRight
        className={`w-4 h-4 shrink-0 text-[#9CA3AF] transition-transform ${isActive ? "rotate-90 text-[#8B55DD]" : ""}`}
      />
    </button>
  </div>
);

// ─── Status checkbox list popout ───────────────────────────────────────────────
const StatusPopout = ({ statuses, onChange, options = FALLBACK_STATUS_OPTIONS }) => {
  const allValues = options.map((s) => s.value);
  const isAll = !statuses || statuses.length === 0;
  // In the "All" state only the "All" row is checked; individual status boxes
  // stay unchecked so a click narrows to that status instead of appearing to
  // clear every other box.
  const isChecked = (value) => !isAll && statuses.includes(value);

  const toggleAll = () => onChange([]); // [] == "All" == no status filter

  const toggleOne = (value) => {
    // From the "All" state, a single click narrows to just that status.
    const current = isAll ? [] : [...statuses];
    let next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    // Empty or full both collapse back to "All" (no filter).
    if (next.length === 0 || next.length === allValues.length) next = [];
    onChange(next);
  };

  const Row = ({ checked, label, onToggle }) => (
    <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="w-[18px] h-[18px] rounded cursor-pointer"
        style={{ accentColor: "#8B55DD" }}
      />
      <span className="text-[14px] font-medium text-[#1A1A1A]">{label}</span>
    </label>
  );

  return (
    <div className="w-[200px]">
      <span className="block text-[13px] text-[#00000080] mb-2 font-medium">Status</span>
      <Row checked={isAll} label="All" onToggle={toggleAll} />
      {options.map((opt) => (
        <Row key={opt.value} checked={isChecked(opt.value)} label={opt.label} onToggle={() => toggleOne(opt.value)} />
      ))}
    </div>
  );
};

// ─── Access radio list popout ─────────────────────────────────────────────────
// Four disjoint buckets, named after what the My Access column shows for each
// so the filter and the table agree.
//
// This used to be called "Source", with the last option labelled "In my
// organization" — which read as a superset (everything in my workspace) when it
// means the opposite: only OTHER people's projects, the ones an admin can see
// but has no access to. Your own projects are in your organization too, so the
// old name described the wrong set entirely. Each option now says what it
// actually selects, and why you can see those rows at all.
const SOURCE_OPTIONS = [
  {
    value: PROJECT_SOURCES.ALL,
    label: "All projects",
    hint: "Everything you can see here",
  },
  {
    value: PROJECT_SOURCES.OWNED,
    label: "Owned by me",
    hint: "You created these",
  },
  {
    value: PROJECT_SOURCES.SHARED,
    label: "Shared with me",
    hint: "You're an editor or reviewer",
  },
  {
    value: PROJECT_SOURCES.ORGANIZATION,
    label: "No access yet",
    hint: "Teammates' projects you can see as an admin",
  },
];

const SOURCE_LABELS = SOURCE_OPTIONS.reduce(
  (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
  {}
);

const SourcePopout = ({ source, onChange, options }) => (
  <div className="w-[260px]">
    <span className="block text-[13px] text-[#00000080] mb-2 font-medium">My access</span>
    {options.map((opt) => (
      <label
        key={opt.value}
        className="flex items-start gap-2.5 py-1.5 cursor-pointer select-none"
      >
        <input
          type="radio"
          name="project-source"
          checked={normalizeSource(source) === opt.value}
          onChange={() => onChange(opt.value)}
          className="w-[18px] h-[18px] cursor-pointer mt-[1px] flex-shrink-0"
          style={{ accentColor: "#8B55DD" }}
        />
        <span className="min-w-0">
          <span className="block text-[14px] font-medium text-[#1A1A1A]">{opt.label}</span>
          <span className="block text-[12px] text-[#00000073] leading-snug">{opt.hint}</span>
        </span>
      </label>
    ))}
  </div>
);

// ─── Compact month calendar for the date popouts (single-date selection) ───────
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const MiniCalendar = ({ startValue, endValue, onSelect, disabled }) => {
  const [viewDate, setViewDate] = useState(() => startValue || new Date());

  useEffect(() => {
    if (startValue) setViewDate(startValue);
  }, [startValue]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay();
  const today = new Date();

  const isSameDay = (a, b) =>
    a && b && a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

  const hasRange = startValue && endValue && startValue.getTime() !== endValue.getTime();

  return (
    <div className={`w-[240px] ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-1 rounded-full hover:bg-gray-100" aria-label="Previous month">
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <span className="text-[13px] font-semibold text-[#1A1A1A]">{MONTHS[month]} {year}</span>
        <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-1 rounded-full hover:bg-gray-100" aria-label="Next month">
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-center text-[11px] font-medium text-gray-400 py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {Array.from({ length: firstWeekday }).map((_, i) => <div key={`e-${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const cellDate = new Date(year, month, day);
          const isStart = isSameDay(cellDate, startValue);
          const isEnd = isSameDay(cellDate, endValue);
          const isEdge = isStart || isEnd;
          const inRange = hasRange && cellDate > startValue && cellDate < endValue;
          const isToday = isSameDay(cellDate, today);

          // Light connecting band drawn behind the range days (incl. endpoints).
          const band = hasRange && (inRange || isStart || isEnd)
            ? `bg-[#F4ECFF] ${isStart ? "rounded-l-full" : ""} ${isEnd ? "rounded-r-full" : ""}`
            : "";

          return (
            <div key={day} className={`flex items-center justify-center ${band}`}>
              <button
                type="button"
                onClick={() => onSelect(cellDate)}
                className={`h-8 w-8 flex items-center justify-center text-[12px] rounded-full transition-colors ${
                  isEdge
                    ? "bg-[#C95EBE] text-white font-semibold"
                    : inRange
                    ? "text-[#8B55DD] font-medium"
                    : isToday
                    ? "text-[#C95EBE] font-semibold hover:bg-gray-50"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Date filter popout (preset list + calendar + Apply) ───────────────────────
const DatePopout = ({ section, onApply }) => {
  const [draft, setDraft] = useState(() => section || emptyDateFilter());

  // Re-seed whenever a different committed section is opened.
  useEffect(() => {
    setDraft(section || emptyDateFilter());
  }, [section]);

  const customStart = draft.preset === "custom" ? parseProjectDate(draft.start) : null;
  const customEnd = draft.preset === "custom" ? parseProjectDate(draft.end) : null;

  const selectPreset = (key) => {
    if (key === "custom") {
      setDraft((d) => ({ ...d, preset: "custom" }));
    } else {
      setDraft({ preset: key, start: null, end: null });
    }
  };

  const selectDay = (date) => {
    const iso = toISODate(date);
    setDraft((d) => {
      const start = parseProjectDate(d.start);
      const end = parseProjectDate(d.end);
      // No start yet, or a complete range already exists → begin a fresh range.
      if (!start || (start && end)) {
        return { preset: "custom", start: iso, end: null };
      }
      // Second pick completes the range, auto-ordering the two endpoints.
      return date < start
        ? { preset: "custom", start: iso, end: d.start }
        : { preset: "custom", start: d.start, end: iso };
    });
  };

  const canApply = draft.preset !== "custom" || !!draft.start;

  return (
    <div className="flex gap-3">
      {/* Preset list */}
      <div className="flex flex-col gap-1 w-[110px] pr-3 border-r border-gray-100">
        {DATE_PRESETS.map((p) => {
          const active = draft.preset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => selectPreset(p.key)}
              className={`text-left px-2.5 py-2 rounded-lg text-[14px] font-medium transition-colors ${
                active ? "bg-[#F4ECFF] text-[#8B55DD]" : "text-[#1A1A1A] hover:bg-gray-50"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Calendar + Apply */}
      <div>
        <MiniCalendar startValue={customStart} endValue={customEnd} onSelect={selectDay} disabled={draft.preset !== "custom"} />
        {draft.preset === "custom" && (
          <p className="text-[11px] text-gray-400 mt-2 px-0.5">
            {draft.start
              ? `${dateFilterLabel(draft)}${draft.end ? "" : " (pick end date)"}`
              : "Select a start and end date"}
          </p>
        )}
        <div className="flex justify-end mt-3">
          <button
            type="button"
            disabled={!canApply}
            onClick={() => onApply(draft)}
            className={`px-5 py-1.5 rounded-md text-[14px] font-semibold transition-colors ${
              canApply ? "bg-[#8B55DD] text-white hover:bg-[#7a48c4]" : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const MyProjectFilterDropdown = ({
  initialFilters = {},
  onApply,
  onClear,
  // Hidden on views that don't mix sources (Archived).
  showSourceFilter = false,
  // True when the list contains org-admin oversight rows, which adds the
  // "In my organization" choice.
  showOrgSource = false,
}) => {
  const sourceOptions = showOrgSource
    ? SOURCE_OPTIONS
    : SOURCE_OPTIONS.filter((opt) => opt.value !== PROJECT_SOURCES.ORGANIZATION);

  const normalize = useCallback((f) => ({
    source: normalizeSource(f?.source),
    statuses: f?.statuses || [],
    bidEndDate: f?.bidEndDate || emptyDateFilter(),
    dateSubmitted: f?.dateSubmitted || emptyDateFilter(),
    // Preserve any unrelated keys (e.g. legacy `tags`) so we don't drop them.
    ...(f?.tags ? { tags: f.tags } : {}),
  }), []);

  const [isOpen, setIsOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null); // 'status' | 'bidEndDate' | 'dateSubmitted'
  const [selected, setSelected] = useState(() => normalize(initialFilters));
  const [statusOptions, setStatusOptions] = useState(FALLBACK_STATUS_OPTIONS);

  // Load the org's enabled project statuses so the Status filter matches what the
  // settings page allows (including custom statuses). Falls back to the hardcoded
  // set on error.
  useEffect(() => {
    let active = true;
    const loadStatuses = async () => {
      const orgId = Cookies.get("selected_org_id");
      if (!orgId) return;
      try {
        const res = await api.get(
          `/api/v1/org/project-statuses/?organization_id=${orgId}`
        );
        const all = [
          ...(res.data?.default || []),
          ...(res.data?.custom || []),
        ].filter((s) => s.is_enabled);
        if (active && all.length) {
          setStatusOptions(
            all.map((s) => ({
              value: s.label,
              label: s.label === "Win" ? "Won" : s.label,
            }))
          );
        }
      } catch (error) {
        console.error("Failed to load status filter options:", error);
        // keep the fallback options
      }
    };
    loadStatuses();
    return () => {
      active = false;
    };
  }, []);

  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const hasActiveFilters =
    (showSourceFilter && normalizeSource(selected.source) !== PROJECT_SOURCES.ALL) ||
    selected.statuses?.length > 0 ||
    selected.bidEndDate?.preset !== "all" ||
    selected.dateSubmitted?.preset !== "all";

  useEffect(() => {
    setSelected(normalize(initialFilters));
  }, [JSON.stringify(initialFilters), normalize]);

  // Close everything when clicking outside the button and the portaled panel.
  useEffect(() => {
    const handleClickOutside = (event) => {
      const outsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const outsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      if (outsideButton && outsideDropdown) {
        setIsOpen(false);
        setOpenSection(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  const updatePosition = useCallback(() => {
    if (buttonRef.current && isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Commit immediately so the table reflects changes live (matches the design,
  // which has no panel-level Apply button).
  const commit = (next) => {
    setSelected(next);
    if (onApply) onApply(next);
  };

  const handleStatusChange = (statuses) => commit({ ...selected, statuses });

  const handleSourceChange = (source) => {
    commit({ ...selected, source });
    setOpenSection(null);
  };

  const handleDateApply = (sectionKey, sectionValue) => {
    commit({ ...selected, [sectionKey]: sectionValue });
    setOpenSection(null);
  };

  const handleClearAll = () => {
    const cleared = normalize({ ...(selected.tags ? { tags: selected.tags } : {}) });
    setSelected(cleared);
    setOpenSection(null);
    if (onClear) onClear();
    else if (onApply) onApply(cleared);
  };

  const toggleSection = (key) => setOpenSection((prev) => (prev === key ? null : key));

  const statusSummary = selected.statuses?.length
    ? selected.statuses.map((v) => (v === "Win" ? "Won" : v)).join(", ")
    : "All";

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="absolute w-[320px] bg-white rounded-xl shadow-[0px_4px_24px_rgba(0,0,0,0.10)] border border-[#E5E7EB] p-5 font-outfit"
      style={{ top: `${dropdownPosition.top}px`, right: `${dropdownPosition.right}px`, zIndex: 2147483647 }}
    >
      {/* Active section popout — anchored to the left of the panel */}
      {openSection && (
        <div className="absolute right-full mr-3 top-0 bg-white rounded-xl shadow-[0px_4px_24px_rgba(0,0,0,0.12)] border border-[#E5E7EB] p-4">
          {openSection === "source" ? (
            <SourcePopout source={selected.source} onChange={handleSourceChange} options={sourceOptions} />
          ) : openSection === "status" ? (
            <StatusPopout statuses={selected.statuses} onChange={handleStatusChange} options={statusOptions} />
          ) : (
            <DatePopout
              section={selected[openSection]}
              onApply={(value) => handleDateApply(openSection, value)}
            />
          )}
        </div>
      )}

      <div className="flex justify-between items-center mb-5">
        <h3 className="text-[16px] font-semibold text-[#1A1A1A]">Filters</h3>
        <button
          type="button"
          onClick={() => { setIsOpen(false); setOpenSection(null); }}
          className="text-[#A1A1AA] hover:text-[#52525B] transition-colors"
        >
          <X className="w-[18px] h-[18px]" />
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {showSourceFilter && (
          <FilterRow
            label="My access"
            value={SOURCE_LABELS[normalizeSource(selected.source)]}
            isActive={openSection === "source"}
            onClick={() => toggleSection("source")}
          />
        )}
        <FilterRow label="Status" value={statusSummary} isActive={openSection === "status"} onClick={() => toggleSection("status")} />
        <FilterRow label="Bid End Date" value={dateFilterLabel(selected.bidEndDate)} isActive={openSection === "bidEndDate"} onClick={() => toggleSection("bidEndDate")} />
        <FilterRow label="Date Submitted" value={dateFilterLabel(selected.dateSubmitted)} isActive={openSection === "dateSubmitted"} onClick={() => toggleSection("dateSubmitted")} />
      </div>

      {hasActiveFilters && (
        <div className="flex justify-end mt-5">
          <button
            type="button"
            onClick={handleClearAll}
            className="text-[13px] font-medium text-[#8B55DD] hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative inline-block font-outfit" ref={buttonRef}>
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => {
            if (!isOpen) updatePosition();
            setIsOpen((o) => !o);
            setOpenSection(null);
          }}
          className={`flex items-center gap-2 px-4 py-1.5 border rounded text-[14px] font-medium transition-colors ${
            isOpen || hasActiveFilters
              ? "border-[#8B55DD] text-[#8B55DD] bg-[#F8F3FF]"
              : "border-[#00000036] text-[#52525B] hover:bg-gray-50"
          }`}
        >
          <FilterIcon /> Filters
        </button>
        {hasActiveFilters && (
          <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white transform translate-x-1/2 -translate-y-1/2"></span>
        )}
      </div>

      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default MyProjectFilterDropdown;
